import React, { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { isAuthenticated } from "../services/login";
import { getStoredValue, safeSessionStorage } from "../services/logics";
import { getSSOProvider } from "../utils/ssoProvider";
import { toast } from "react-toastify";
import RotatingLoader from "../assets/RotatingLoader.jsx";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { useDispatch } from "react-redux";
import { setSessionExpired } from "../redux/sessionSlice";

// Redirect component to handle toast and navigation
const RedirectToDocumentPage = () => {
  useEffect(() => {
    toast.info("To proceed, please upload a Document. This step is necessary for draft generation.", {
      toastId: "document-upload-info",
    });
  }, []);

  return <Navigate to="/policies-page" replace />;
};
const RedirectToIntegrationPage = ({ getSetupReminderMessage }) => {
  useEffect(() => {
    if (!safeSessionStorage.getItem("oauth_params_on_load")) {
      toast.info(getSetupReminderMessage(), {
        toastId: "integration-page-info",
      });
    }
  }, []);

  return <Navigate to="/integration-page" replace />;
};
// The /intro-app screen (intro video before store selection) is sp-lite only
// for now - servicepoints.nl keeps going straight to /select-store.
const isSpLite = getSSOProvider() === "sp_lite";

const PrivateRoute = ({ children, isAdminRoute }) => {
  const ldClient = useLDClient();
  const token = getStoredValue("token");
  const isSuperAdmin = getStoredValue("is_super_admin");
  const location = useLocation();
  const [authStatus, setAuthStatus] = useState("checking");
  const isLoggedIn = getStoredValue("isLoggedIn");
  const store_id = getStoredValue("storeid");
  const documentUploaded = getStoredValue("documentUploaded") === "true";
  const onboarded = getStoredValue("isonboarded") === "true";
  const emailaccount = getStoredValue("emailaccount") === "true";
  const shopifyaccount = getStoredValue("shopifyaccount") === "true";
  // const isAddClientPage = location.pathname.includes("/add-client");
  const isSelectStorePage = location.pathname.includes("/select-store");
  const isIntroAppPage = location.pathname.includes("/intro-app");
  const isWelcomePage = location.pathname.includes("/welcome-page");

  const [ldReady, setLdReady] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    const checkAuth = async () => {
      let authResult;
      try {
        authResult = await isAuthenticated();
        const { success, transient } = authResult;
        setAuthStatus(success ? "authenticated" : "unauthenticated");
        // Storage still says "logged in" but the session is dead: hand over to SessionExpiredGate
        // so the page below is never rendered with buttons that can only 401. A failed refresh
        // already does this; this also covers every other way isAuthenticated can fail. Not for a
        // transient failure (aborted by a reload, offline): SessionExpiredGate wipes storage, which
        // would log out a session that is still valid.
        if (!success && !transient && getStoredValue("isLoggedIn") === "true") dispatch(setSessionExpired(true));
      } catch (error) {
        console.error("Authentication error:", error);
        setAuthStatus("unauthenticated");
        if (getStoredValue("isLoggedIn") === "true") dispatch(setSessionExpired(true));
        setLdReady(true);
        return;
      }

      // LaunchDarkly identify() is analytics/feature-flag plumbing, not part of the
      // session check above - it must never be able to flip a successful auth into
      // a session-expired dispatch. It used to share the try/catch above: an
      // identify() rejection (LD client not yet ready, a network hiccup) landed in
      // the same catch as a real auth failure and showed "Session Has Expired" even
      // though isAuthenticated() had just succeeded.
      const { success, user, store, role } = authResult;
      if (success && ldClient) {
        try {
          await ldClient.identify({
            kind: "multi",
            user: {
              key: user?.email,
              name: `${user?.first_name}${user?.last_name ? ` ${user?.last_name}` : ""}`,
              email: user?.email,
              custom: {
                // User details
                role: role,
                user_id: user?.id,

                // Store details
                store_id: store?.id,
                store_numeric_id: store?.store_id,
                store_name: store?.store_allias_name || store?.name,

                // Client details inside store
                client_id: store?.client?.id,
                client_name: store?.client?.name,
                account_detail_id: store?.client?.account_detail_id,
              },
            },
            store_id: {
              key: store?.id,
            },
          });
        } catch (ldError) {
          console.error("LaunchDarkly identify failed:", ldError);
        }
      }
      setLdReady(true);
    };

    checkAuth();
  }, []);

  const getSetupReminderMessage = () => {
    const incompleteSteps = [];
    if (!emailaccount) incompleteSteps.push("an Email Account");
    if (!shopifyaccount) incompleteSteps.push("a Shopify Store");
    if (incompleteSteps.length > 0) {
      return `To proceed, please link ${incompleteSteps.join(" and ")}. This step is necessary for draft generation..`;
    }
  };

  if (authStatus === "checking" || !ldReady) {
    return (
      <div className="flex items-center justify-center h-screen">
        <RotatingLoader />
      </div>
    );
  }

  if (authStatus === "unauthenticated" && isLoggedIn !== "true") {
    // If user is a super admin, redirect to admin signin, otherwise to regular signin
    if (isSuperAdmin === "True" && store_id === "undefined") {
      return <Navigate to="/admin-signin" state={{ from: location }} replace />;
    }
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  if (!token) {
    // If user is a super admin, redirect to admin signin, otherwise to regular signin
    if (isSuperAdmin === "True" && store_id === "undefined") {
      return <Navigate to="/admin-signin" replace />;
    }
    return <Navigate to="/signin" replace />;
  }

  if (isSuperAdmin === "True" && store_id === "undefined") {
    return <Navigate to="/admin/dashboard-page" replace />;
  }

  if (isSuperAdmin === "False") {
    // Redirect to add client page if neither onboarded nor storeId exists
    if (!onboarded) {
      if (store_id === "undefined" && !isSelectStorePage && !isIntroAppPage) {
        return <Navigate to={isSpLite ? "/intro-app" : "/select-store"} replace />;
      }

      // Redirect to welcome page if user has not completed onboarding
      if (store_id !== "undefined" && !isWelcomePage && !isSelectStorePage) {
        return <Navigate to="/welcome-page" replace />;
      }
    }

    // Redirect users on the welcome page to the document page if onboarding is completed
    if (onboarded && isWelcomePage) {
      return <Navigate to="/dashboard" replace />;
    }
    if (!documentUploaded && onboarded) {
      if (!location.pathname.includes("/policies-page") && !location.pathname.includes("/select-store")) {
        return <RedirectToDocumentPage />;
      }
    } else if (!window.isOAuthFlow && (!emailaccount || !shopifyaccount) && onboarded) {
      if (!location.pathname.includes("/integration-page") && !location.pathname.includes("/select-store")) {
        return <RedirectToIntegrationPage getSetupReminderMessage={getSetupReminderMessage} />;
      }
    }
    // Redirect from root path to the document page if no other redirects apply
    if (location.pathname === "/") {
      return <Navigate to="/dashboard" replace />;
    }
    return <Outlet />;
  }

  if (isSuperAdmin === "True" && store_id !== "undefined") {
    if (location.pathname === "/") {
      return <Navigate to="/dashboard" replace />;
    }
    return <Outlet />;
  }

  return <Navigate to="/error" replace />;
};

export default PrivateRoute;
